import { makeContentContext, makeInputContext, makeInputValueContext } from '@objectiv/schema';
import { useInputChangeEventTracker, useVisibleEventTracker } from '@objectiv/tracker-react';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

export const ASSISTANT_CHAT_TRACKING_ID = 'assistant-chat';

type ChatActivityTrackingData = {
  input?: string;
  response?: string;
  sessionId?: string;
};

export type ChatActivityEventType = 'input_changed' | 'response_received' | 'input_submit';

export const useChatActivityTracking = () => {
  const trackInputChange = useInputChangeEventTracker({
    locationStack: [makeInputContext({ id: ASSISTANT_CHAT_TRACKING_ID })],
  });

  const trackVisibleEvent = useVisibleEventTracker();

  const trackSlideVisibleEvent = useCallback(
    (data: string) => {
      return trackVisibleEvent({
        locationStack: [makeContentContext({ id: data })],
      });
    },
    [trackVisibleEvent],
  );

  const trackChatActivityData = useCallback(
    async (data: ChatActivityTrackingData, event: ChatActivityEventType) => {
      return await trackInputChange({
        locationStack: [makeContentContext({ id: event })],
        globalContexts: [
          makeInputValueContext({
            id: ASSISTANT_CHAT_TRACKING_ID,
            value: JSON.stringify(data),
          }),
        ],
      });
    },
    [trackInputChange],
  );

  const trackEvent = useCallback(
    (eventType: ChatActivityEventType, data: ChatActivityTrackingData) => {
      void trackChatActivityData(data, eventType);
    },
    [trackChatActivityData],
  );

  const trackDebouncedEvent = useMemo(
    () =>
      debounce(
        (eventType: ChatActivityEventType, data: ChatActivityTrackingData) => {
          trackEvent(eventType, data);
        },
        400,
        { leading: true, trailing: true },
      ),
    [trackEvent],
  );

  useEffect(() => {
    return () => {
      trackDebouncedEvent.cancel();
    };
  }, [trackDebouncedEvent]);

  return {
    trackEvent,
    trackDebouncedEvent,
    trackSlideVisibleEvent,
  };
};
